import React, { useEffect, useRef, useState } from 'react'
import { Grommet } from 'grommet'
import { CsvDownloadMenu, themes } from 'secondstep-components'
import { HistoricalReport } from 'store/reports/reducer'
import { createLessonReportDownloadData } from 'store/reports/selector'
import { DOWNLOAD_LESSON_REPORT_TEXT } from './constants'

interface K8ReportDownloadMenuProps {
  buttonText?: string
  historicalReports: HistoricalReport[]
  isFetchingCurrentReport: boolean
  isFetchingHistoricalReports: boolean
  onFetchCurrentYearReport: () => void
  reportUrl?: string | null
  siteId: number
}

const K8ReportDownloadMenu: React.FC<K8ReportDownloadMenuProps> = ({
  buttonText = DOWNLOAD_LESSON_REPORT_TEXT,
  historicalReports,
  isFetchingCurrentReport,
  isFetchingHistoricalReports,
  onFetchCurrentYearReport,
  reportUrl,
  siteId,
}) => {
  const [isDownloadingHistoricalReport, setDownloadingHistoricalReport] =
    useState(false)
  const pendingCurrentYearDownload = useRef(false)

  useEffect(() => {
    if (!pendingCurrentYearDownload.current || !reportUrl) {
      return
    }

    const downloadLink = document.createElement('a')
    downloadLink.href = reportUrl
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    pendingCurrentYearDownload.current = false
  }, [reportUrl])

  const handleCurrentYearClick = () => {
    pendingCurrentYearDownload.current = true
    onFetchCurrentYearReport()
    return true
  }

  const handleHistoricalClick = async (
    data: unknown[] | undefined,
    filename: string,
    url?: string,
  ) => {
    const csvData = createLessonReportDownloadData(
      data,
      null,
      siteId,
      filename,
      filename?.split('.').pop() || 'csv',
    )

    if (!url && !data) {
      return false
    }

    if (!data) {
      try {
        setDownloadingHistoricalReport(true)

        const link = document.createElement('a')
        link.href = url as string
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return true
      } catch (error) {
        console.error('Error downloading file:', error)
        return false
      } finally {
        setDownloadingHistoricalReport(false)
      }
    }

    return csvData
  }

  const isFetching =
    isFetchingCurrentReport ||
    isFetchingHistoricalReports ||
    isDownloadingHistoricalReport

  const hasNoReportsToSelect =
    !isFetchingHistoricalReports && historicalReports.length === 0

  return (
    <Grommet theme={themes.baseTheme}>
      <CsvDownloadMenu
        buttonText={buttonText}
        disabledSelectButton={isFetching || hasNoReportsToSelect}
        dropdownAlign="right"
        hasScrollableHistoricalReports={true}
        historicalReports={historicalReports}
        onClickCurrent={() => Promise.resolve(true)}
        onClickCurrentYear={handleCurrentYearClick}
        onClickHistorical={handleHistoricalClick}
        showDownloadIcons={false}
        showLoadingText={false}
      />
    </Grommet>
  )
}

export default K8ReportDownloadMenu
